import { HdTransitSegment, HdTransitStation } from './transit.models';

/**
 * Exacte retrograde-perioden van een hemellichaam, afgeleid uit de stations.
 *
 * BELANGRIJK: de `retrograde`-vlag van een segment is enkel de richting op het
 * MIDDEN van dat segment (backend-solver). Rond een station blijft een planeet
 * lang in dezelfde lijn hangen, dus precies daar bevat één segment zowel een
 * direct als een retrograde stuk. Wie de vlag voor het hele segment gebruikt,
 * arceert een blok helemaal of helemaal niet, ook al keert de planeet er
 * halverwege (de lanes toonden zo "geen retrograde" terwijl de planeet al
 * retrograde liep, met het station-bolletje vaak buiten beeld). Alle weergaves
 * van "is retrograde op moment t" moeten daarom deze perioden gebruiken, niet
 * de segment-vlag. Regressietests: `transit-retrograde.utils.spec.ts`,
 * `hd-ephemeris-lanes.utils.spec.ts` (gedeeltelijke arcering) en
 * `hd-transit-day-flow.builder.spec.ts` (℞ op het juiste tijdstip).
 */

/** Halfopen tijdvak [fromMs, toMs) waarin het hemellichaam retrograde loopt. */
export interface HdTransitRetrogradeSpan {
  readonly fromMs: number;
  readonly toMs: number;
}

/**
 * Richting op het begin van een venster. De backend levert die rechtstreeks
 * (`retrogradeAtFromUtc`); zonder dat veld (oudere backend) volgt hij uit het
 * eerste station in het venster (vóór "keert direct" liep hij retrograde), en
 * als laatste redmiddel uit de segment-vlag op dat moment.
 */
export function resolveRetrogradeAtWindowStart(
  retrogradeAtFromUtc: boolean | null | undefined,
  stations: readonly HdTransitStation[],
  segments: readonly HdTransitSegment[],
  fromMs: number,
  toMs: number,
): boolean {
  if (retrogradeAtFromUtc != null) return retrogradeAtFromUtc;
  const firstStation = stations.find(station => station.atUtc.valueOf() >= fromMs && station.atUtc.valueOf() < toMs);
  if (firstStation != null) return firstStation.turnsTo === 'direct';
  const segmentAtStart = segments.find(
    segment => segment.fromUtc.valueOf() <= fromMs && segment.toUtc.valueOf() > fromMs,
  );
  return segmentAtStart?.retrograde ?? false;
}

/**
 * De retrograde-perioden binnen [fromMs, toMs): vertrekt van de richting op
 * `fromMs` en wisselt op elk station in het venster. Verwacht de stations
 * oplopend; een station dat de richting niet wijzigt (afronding pal op de
 * vensterrand) wordt genegeerd.
 */
export function buildRetrogradeSpans(
  retrogradeAtFrom: boolean,
  stations: readonly HdTransitStation[],
  fromMs: number,
  toMs: number,
): HdTransitRetrogradeSpan[] {
  const spans: HdTransitRetrogradeSpan[] = [];
  let retrogradeSinceMs: number | undefined = retrogradeAtFrom ? fromMs : undefined;
  for (const station of stations) {
    const atMs = station.atUtc.valueOf();
    if (atMs < fromMs || atMs >= toMs) continue;
    if (station.turnsTo === 'retrograde') {
      retrogradeSinceMs ??= atMs;
      continue;
    }
    if (retrogradeSinceMs == null) continue;
    if (atMs > retrogradeSinceMs) spans.push({ fromMs: retrogradeSinceMs, toMs: atMs });
    retrogradeSinceMs = undefined;
  }
  if (retrogradeSinceMs != null && toMs > retrogradeSinceMs) spans.push({ fromMs: retrogradeSinceMs, toMs });
  return spans;
}

/** De delen van de perioden binnen [fromMs, toMs) (lege delen vallen weg). */
export function clipRetrogradeSpans(
  spans: readonly HdTransitRetrogradeSpan[],
  fromMs: number,
  toMs: number,
): HdTransitRetrogradeSpan[] {
  const clipped: HdTransitRetrogradeSpan[] = [];
  for (const span of spans) {
    const clipFromMs = Math.max(span.fromMs, fromMs);
    const clipToMs = Math.min(span.toMs, toMs);
    if (clipToMs > clipFromMs) clipped.push({ fromMs: clipFromMs, toMs: clipToMs });
  }
  return clipped;
}

/** Sorteert en voegt aansluitende of overlappende perioden samen (bv. per dag geknipte stukken). */
export function mergeRetrogradeSpans(spans: readonly HdTransitRetrogradeSpan[]): HdTransitRetrogradeSpan[] {
  const sorted = [...spans].sort((a, b) => a.fromMs - b.fromMs);
  const merged: HdTransitRetrogradeSpan[] = [];
  for (const span of sorted) {
    const last = merged[merged.length - 1];
    if (last != null && span.fromMs <= last.toMs) {
      merged[merged.length - 1] = { fromMs: last.fromMs, toMs: Math.max(last.toMs, span.toMs) };
    } else {
      merged.push(span);
    }
  }
  return merged;
}

/** true als het hemellichaam op `timeMs` retrograde loopt. */
export function isRetrogradeAt(spans: readonly HdTransitRetrogradeSpan[], timeMs: number): boolean {
  return spans.some(span => span.fromMs <= timeMs && timeMs < span.toMs);
}

/**
 * true als het hemellichaam over heel [fromMs, toMs) retrograde loopt. Verwacht
 * samengevoegde perioden ({@link mergeRetrogradeSpans}): één periode moet het
 * hele vak dekken.
 */
export function isRetrogradeThroughout(spans: readonly HdTransitRetrogradeSpan[], fromMs: number, toMs: number): boolean {
  if (toMs <= fromMs) return isRetrogradeAt(spans, fromMs);
  return spans.some(span => span.fromMs <= fromMs && toMs <= span.toMs);
}
